import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/LanguageContext";
import axios from "axios";

const SHARED_API = process.env.REACT_APP_API_URL;

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name);
      // Track signup from shared page
      const sharedHash = localStorage.getItem("shared_recipe_hash");
      if (sharedHash) {
        axios.post(`${SHARED_API}/api/shared/signup`, { recipe_hash: sharedHash }).catch(() => {});
        localStorage.removeItem("shared_recipe_hash");
      }
      navigate("/generate");
    } catch (err) {
      setError(err.response?.data?.detail || t("error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
              src="https://customer-assets.emergentagent.com/job_flavor-forge-68/artifacts/rz0qyx4s_Firefly_GeminiFlash%202.png"
              alt="What To Cook Logo"
              className="w-14 h-14 rounded-xl mx-auto mb-4"
            />
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-white">
            {t("registerTitle")}
          </h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div data-testid="register-error" className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3">
              {error}
            </div>
          )}
          <div>
            <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-stone-500 mb-2">
              {t("name")}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="register-name"
              required
              className="w-full px-4 py-3 bg-[#1C1917] border border-white/10 rounded-xl text-white placeholder-stone-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-stone-500 mb-2">
              {t("email")}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email"
              required
              className="w-full px-4 py-3 bg-[#1C1917] border border-white/10 rounded-xl text-white placeholder-stone-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-stone-500 mb-2">
              {t("password")}
            </label>
            <div className="relative">
              <input
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password"
                required
                className="w-full px-4 py-3 bg-[#1C1917] border border-white/10 rounded-xl text-white placeholder-stone-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors pr-12"
                placeholder="********"
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit"
            className="w-full py-3 bg-amber-500 text-stone-950 font-semibold rounded-full hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t("loading") : t("register")}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-stone-500">
          {t("hasAccount")}{" "}
          <Link to="/login" data-testid="goto-login" className="text-amber-500 hover:text-amber-400 font-medium">
            {t("login")}
          </Link>
        </p>
      </div>
    </div>
  );
}
